import { inject, Injectable, Signal, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { environment } from '@unified-ui/environments';
import { authSelectAnalyticData, authSelectAuthUserEmail, authSelectAuthUsername } from '@unified-ui/shared/auth-lib';
import { initialize, LDClient, LDContext, LDFlagSet, LDOptions } from 'launchdarkly-js-client-sdk';
import { combineLatest, filter, take } from 'rxjs';
import { FeatureFlagKey, FeatureFlagKeyType, IFeatureFlag } from './feature-flags.types';

@Injectable({
    providedIn: 'root'
})
export class FeatureFlagsService {
    private _ldClient: LDClient | null = null;
    private clientId = environment.launchDarklyClientId;
    private store = inject(Store);
    private _featureFlags: IFeatureFlag<any> = {} as IFeatureFlag<any>;

    constructor() {
        this._initAllFlags();
        // Automatically initialize the client when the service is first used
        combineLatest([this.store.select(authSelectAuthUserEmail), this.store.select(authSelectAuthUsername), this.store.select(authSelectAnalyticData)])
            .pipe(
                filter(([userEmail]) => !!userEmail),
                take(1)
            )
            .subscribe(([userEmail, userName, analyticData]) => {
                this.initializeClient({
                    kind: 'user',
                    key: userEmail,
                    name: userName,
                    // custom is deprecated, we will have to change settings to send orgIg at the base level
                    // orgId: analyticData?.orgId,
                    custom: {
                        orgId: analyticData?.orgId
                    }
                });
            });
    }

    /**
     * Initialize the LaunchDarkly client with the user context
     * @param userContext The user context to initialize the client with
     * @param initialFlags Optional initial flags to bootstrap the client with
     */
    public initializeClient(userContext: LDContext, initialFlags?: LDFlagSet): void {
        const options: LDOptions = { bootstrap: initialFlags, sendEvents: false };
        this._ldClient = initialize(this.clientId, userContext, options);

        // Update all flags when the client is ready
        this._ldClient.on('ready', () => {
            this._setAllFlags();
        });

        // Update a specific flag when it changes
        this._ldClient.on('change', changes => {
            Object.keys(changes).forEach(key => {
                this._setFeatureFlag(key as FeatureFlagKeyType, changes[key].current);
            });
        });
    }

    /**
     * Initialize all feature flags with a signal
     */
    private _initAllFlags() {
        for (const key of Object.values(FeatureFlagKey)) {
            this._featureFlags[key] = signal(undefined);
        }
    }

    /**
     * Update all feature flags from the LaunchDarkly client
     */
    private _setAllFlags(): void {
        if (!this._ldClient) return;
        const allFlags = this._ldClient.allFlags();
        Object.keys(allFlags).forEach(key => {
            this._featureFlags[key as FeatureFlagKeyType]?.set(allFlags[key]);
        });
    }

    /**
     * Update a specific feature flag from the LaunchDarkly client
     * @param key The key of the feature flag to update
     * @param value The value to set the feature flag to
     */
    private _setFeatureFlag(key: FeatureFlagKeyType, value: any): void {
        this._featureFlags[key]?.set(value);
    }

    /**
     * Retrieves the readonly signal associated with a specific feature flag key.
     * @param {FeatureFlagKeyType} key - The key of the feature flag whose signal should be retrieved.
     * @return {Signal} The readonly signal corresponding to the specified feature flag key.
     */
    public getFeatureFlagSignal<T>(key: FeatureFlagKeyType): Signal<T | undefined> {
        return this._featureFlags[key].asReadonly();
    }
}
